/**
 * 5-dot connection status row for Integrations page (Phase 5, 2026-05-30).
 *
 * Snippet · Google Ads · Meta Ads · WhatsApp · Google Analytics
 *
 * Green dot = connected (or verified for snippet). Gray dot = not yet.
 * GA mirrors Google Ads state (shared OAuth via Google Data Manager API).
 *
 * Reads everything from useAuth().me — no extra query.
 */

import { useAuth } from '@/lib/useAuth';

interface Dot {
  label: string;
  connected: boolean;
}

export function IntegrationsStatusOverview() {
  const { me } = useAuth();

  const dots: Dot[] = [
    {
      label: 'Snippet',
      // Verified OR explicitly skipped via no_website both count as
      // "addressed" for the status overview (banner suppression follows
      // the same rule).
      connected: !!me?.snippet_verified_at || !!me?.no_website_at,
    },
    { label: 'Google Ads', connected: !!me?.google_ads_customer_id },
    { label: 'Meta Ads', connected: !!me?.facebook_pixel_id },
    { label: 'WhatsApp', connected: !!me?.waba_id },
    {
      label: 'Google Analytics',
      // Mirrors Google Ads — GA firing rides on the same OAuth.
      connected: !!me?.google_ads_customer_id,
    },
  ];

  const allConnected = dots.every((d) => d.connected);

  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
      <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {allConnected ? 'All connected' : 'Connection status'}
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {dots.map((d) => (
          <div key={d.label} className="flex items-center gap-2">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                d.connected ? 'bg-green-500' : 'bg-dark/15'
              }`}
            />
            <span className="text-sm font-semibold text-dark">{d.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
